import { type ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";
import type { FrameworkResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { DragMark } from "@/components/ui/DragHandle";
import { CAPTION_LINE } from "@/components/ui/marks";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Spinner } from "@/components/ui/Spinner";
import { Note } from "@/components/ui/Note";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  useConfirmFramework,
  useDdFramework,
  useReplaceFramework,
} from "@/hooks/use-dd-framework";
import { useInventionNames } from "@/hooks/use-invention";
import { useClaimStrategy } from "@/hooks/use-claim-strategy";
import { useFigures } from "@/hooks/use-figures";
import { claimTitle } from "@/components/patterns/ThesisLists";

import { claimsBeneath, withClaimsWords } from "./claims-beneath";
import { StageFooter } from "../layout/StageFooter";
import { StepBanner } from "../notifications/StepChanges";
import { destinationAfter } from "../stage-vocabulary";
import { AddField } from "./AddField";
import { CommitCard } from "./CommitCard";
import { ProposeAgain } from "./ProposeAgain";
import {
  ProposalState,
  proposalRunning,
  useFirstProposal,
} from "./ProposalState";
import { FrameworkDrag, useArrangedLanes } from "./FrameworkDrag";
import {
  SECTIONS,
  addSection,
  holdsDefinitions,
  laneOf,
  placeUnplaced,
  reinstate,
  reinstateInto,
  toEdits,
} from "./framework-edits";
import { NOWHERE, type Send, sectionActions } from "./section-actions";
import { RemovedFold } from "./RemovedFold";
import { ThesisCard } from "./ThesisCard";
import { SectionCard, useFolds } from "./SectionCard";
import { type Labels, spoken } from "./SubsectionRow";
import { UnplacedTray, unplacedOf, unplacedWords } from "./UnplacedTray";
import { TEXT_LIMITS } from "@/lib/text-limits";
import { listed } from "@/lib/plural";
import { ReadProblem } from "@/components/patterns/ReadProblem";

const SOURCES = [
  "Invention elements",
  "Novelty positions",
  "Claims · categories",
  "Figures · descriptions",
  "Classifications",
  "Patent Studio Questions",
];
const SECTION_GONE = "That section is gone. Choose another.";

/** The sources the plan is read from, Propose again to read them again, and what changed since it last did. */
function GeneratedFrom({
  taskId,
  data,
}: {
  taskId: string;
  data: FrameworkResponse;
}) {
  return (
    <>
      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        <span className={CAPTION_LINE}>Generated from:</span>
        {SOURCES.map((source) => (
          <StatusPill key={source} tone="neutral" size="sm">
            {source}
          </StatusPill>
        ))}
        <span className="ml-auto">
          <ProposeAgain taskId={taskId} data={data} />
        </span>
      </div>
      <ChangedSince data={data} />
    </>
  );
}

/**
 * The figures and claims that changed after Praxis proposed the framework, named so the attorney
 * can propose again: what Praxis wrote about them was read from the earlier words.
 */
function ChangedSince({ data }: { data: FrameworkResponse }) {
  const { figures, claims } = data.changed_since_proposal;
  const named = [
    ...figures.map((label) => `FIG. ${label}`),
    ...claims.map((number) => `claim ${String(number)}`),
  ];
  if (named.length === 0) return null;
  return (
    <Note tone="warning" role="status" className="mt-2 text-detail">
      {`${listed(named)} changed after this framework was proposed. Propose again to read the new words; your own edits are kept.`}
    </Note>
  );
}

function useLabels(
  taskId: string,
  response: FrameworkResponse | undefined,
): Labels {
  const figures = useFigures(taskId);
  const claims = useClaimStrategy(taskId);
  const names = useInventionNames(taskId);
  return useMemo(() => {
    const terms = new Map(
      (names.data?.terms ?? []).map((term) => [term.thing_id, term.term]),
    );
    const thumbnails = new Map(
      (figures.data?.figures ?? []).map((f) => [f.file_id, f.thumbnail_url]),
    );
    const views = new Map(
      (figures.data?.definitions?.figures ?? []).map((d) => [
        d.file_id,
        d.description,
      ]),
    );
    const titles = new Map(
      (claims.data?.claims ?? []).map((c) => [c.claim_id, claimTitle(c)]),
    );
    return {
      figure: (fileId) => response?.figure_labels[fileId],
      claim: (claimId) => response?.claim_numbers[claimId],
      thumbnail: (fileId) => thumbnails.get(fileId),
      view: (fileId) => views.get(fileId) || undefined,
      claimTitle: (claimId) => titles.get(claimId) || undefined,
      category: (claimId) => response?.claim_categories[claimId],
      beneath: (claimId) =>
        withClaimsWords(claimsBeneath(claims.data?.claims ?? [], claimId)),
      recital: (recitalId) => terms.get(recitalId),
      rangesNow: (subsectionId) =>
        (response?.ranges_now ?? []).filter(
          (read) => read.subsection_id === subsectionId,
        ),
    };
  }, [figures.data, claims.data, names.data, response]);
}

function Footer({ taskId, confirmed }: { taskId: string; confirmed: boolean }) {
  const navigate = useNavigate();
  const next = destinationAfter(taskId, "planning-outline");
  return (
    <StageFooter
      testId="specification-footer"
      heading="Generate the patent draft"
      detail={
        confirmed
          ? "The Detailed Description is drafted in the confirmed order. After generation, Patent Studio checks the draft against this framework and names any section or figure that is missing."
          : "Confirm the framework to begin drafting."
      }
      ready={confirmed}
      label="Continue"
      pendingLabel="Continuing…"
      onSubmit={() => {
        if (next !== undefined) void navigate(next);
      }}
      sticky
      actionTrail={<ArrowRight aria-hidden />}
      // The page draws no suggested change of its own, so it only points to an earlier step's.
      above={<StepBanner taskId={taskId} step="specification" pointsOnly />}
    />
  );
}

/** How a drag names what it is moving or where it is going, as the page shows it. */
function namer(response: FrameworkResponse, labels: Labels) {
  const sections = response.framework.sections ?? [];
  const titles = new Map(sections.map((s) => [s.section_id, s.title]));
  const lines = new Map(
    sections.flatMap((s) =>
      (s.subsections ?? []).map(
        (sub) => [sub.subsection_id, spoken(sub, labels)] as const,
      ),
    ),
  );
  return (id: string): string => {
    const [kind, ...rest] = id.split(":");
    const tail = rest.join(":");
    if (kind === "into" || kind === "lane")
      return titles.get(tail) ?? "a section";
    if (kind === "unplaced") {
      const [itemKind, ...itemId] = tail.split(":");
      return unplacedWords(
        {
          kind: itemKind === "claim" ? "claim" : "figure",
          id: itemId.join(":"),
        },
        labels,
      );
    }
    return titles.get(id) ?? lines.get(id) ?? "an item";
  };
}

/** The header over the sections: what they are, and the act that folds or opens them all. */
function SectionsHeader({
  heading,
  anyOpen,
  onFoldAll,
}: {
  heading: ReactNode;
  /** Whether any card is open, which decides what the act does and says. */
  anyOpen: boolean;
  onFoldAll: () => void;
}) {
  return (
    <div className="mt-6 flex items-center gap-2">
      <MicroLabel as="h2" className="flex-1">
        {heading}
      </MicroLabel>
      <Button
        variant="secondary"
        size="dense"
        onClick={() => {
          onFoldAll();
        }}
      >
        {anyOpen ? "Collapse all" : "Expand all"}
      </Button>
    </div>
  );
}

/** What the header says: how to move the sections, or that they are confirmed or held still. */
function sectionsHeading(readOnly: boolean, confirmed: boolean): ReactNode {
  if (!readOnly) {
    return (
      <>
        Sections · drag <DragMark /> to reorder · collapse sections to move them
        more easily
      </>
    );
  }
  return confirmed ? "Sections · confirmed" : "Sections";
}

/** The sections in the order the page shows them, which runs ahead of the server's during a drag. */
function Plan({
  response,
  labels,
  readOnly,
  send,
}: {
  response: FrameworkResponse;
  labels: Labels;
  readOnly: boolean;
  send: Send;
}) {
  const lanes = useArrangedLanes(toEdits(response.framework));
  const served = response.framework.sections ?? [];
  const byId = new Map(served.map((s) => [s.section_id, s]));
  const lines = new Map(
    served.flatMap((s) =>
      (s.subsections ?? []).map((sub) => [sub.subsection_id, sub] as const),
    ),
  );
  const sections = (lanes[SECTIONS] ?? []).flatMap((id) => byId.get(id) ?? []);
  const folds = useFolds(sections.map((s) => s.section_id));
  const orphans = new Set(response.orphans);
  const unplaced = unplacedOf(response);
  const definitionsHeld = holdsDefinitions(response.framework);
  const choices = served.map((s) => ({ id: s.section_id, title: s.title }));
  return (
    <>
      <SectionsHeader
        heading={sectionsHeading(readOnly, response.confirmed_current)}
        anyOpen={folds.anyOpen}
        onFoldAll={folds.foldAll}
      />
      <div className="mt-3 space-y-3">
        {sections.map((section, index) => (
          <SectionCard
            key={section.section_id}
            section={section}
            ordered={(lanes[laneOf(section.section_id)] ?? []).flatMap(
              (id) => lines.get(id) ?? [],
            )}
            index={index}
            folding={folds.of(section.section_id)}
            labels={labels}
            orphans={orphans}
            readOnly={readOnly}
            others={choices.filter((c) => c.id !== section.section_id)}
            actions={sectionActions(section, send, {
              offerDefinitions: index === 0 && !definitionsHeld,
            })}
          />
        ))}
      </div>
      {!readOnly && (
        <div className="mt-3">
          <AddField
            placeholder="Add a section"
            action="Add section"
            maxLength={TEXT_LIMITS.sectionTitle}
            onAdd={({ text: title }, putBack) => {
              const id = crypto.randomUUID();
              send((edits) => addSection(edits, title, id), { putBack });
            }}
          />
        </div>
      )}
      {unplaced.length > 0 && (
        <UnplacedTray
          items={unplaced}
          labels={labels}
          sections={choices}
          readOnly={readOnly}
          onPlace={(item, sectionId) => {
            const id = crypto.randomUUID();
            send((edits) => placeUnplaced(edits, item, sectionId, id));
          }}
        />
      )}
    </>
  );
}

/** How the page sends an edit: on the task's write queue, against the latest framework. */
function useSend(taskId: string): Send {
  const replace = useReplaceFramework(taskId);
  return (edit, options = {}) => {
    replace.mutate(
      {
        edit: (framework) => edit(toEdits(framework)),
        refused: options.refused,
        control: options.control,
      },
      { onError: () => options.putBack?.() },
    );
  };
}

function Loaded({ taskId, data }: { taskId: string; data: FrameworkResponse }) {
  const send = useSend(taskId);
  const confirm = useConfirmFramework(taskId);
  const labels = useLabels(taskId, data);
  const [editing, setEditing] = useState(false);
  const propose = useFirstProposal(taskId, data);
  const running = proposalRunning(data.proposal);
  const confirmed = data.confirmed_current && !editing;
  // A proposal running over sections leaves them on the page, held still until it lands.
  const readOnly = confirmed || running;
  const removed = data.framework.removed ?? [];
  const plan = (
    <Plan response={data} labels={labels} readOnly={readOnly} send={send} />
  );
  return (
    <>
      <ThesisCard taskId={taskId} />
      <GeneratedFrom taskId={taskId} data={data} />
      <ProposalState
        data={data}
        onRetry={() => {
          propose.mutate();
        }}
      />
      {running &&
      (data.framework.sections ?? []).length === 0 ? null : readOnly ? (
        plan
      ) : (
        <FrameworkDrag
          edits={toEdits(data.framework)}
          name={namer(data, labels)}
          carried={(id) => namer(data, labels)(id)}
          send={send}
        >
          {plan}
        </FrameworkDrag>
      )}
      {removed.length > 0 && (
        <RemovedFold
          removed={removed}
          labels={labels}
          sectionTitle={(id) =>
            data.framework.sections?.find((s) => s.section_id === id)?.title
          }
          readOnly={readOnly}
          needsTarget={new Set(data.reinstate_needs_target)}
          sections={(data.framework.sections ?? []).map((s) => ({
            id: s.section_id,
            title: s.title,
          }))}
          onReinstate={(item) => {
            send((edits) => reinstate(edits, item), { refused: NOWHERE });
          }}
          onPlace={(item, sectionId) => {
            send((edits) => reinstateInto(edits, item, sectionId), {
              refused: SECTION_GONE,
            });
          }}
        />
      )}
      <CommitCard
        response={data}
        labels={labels}
        editing={editing}
        confirming={confirm.isPending}
        held={running}
        onConfirm={() => {
          confirm.mutate(undefined, {
            onSuccess: () => {
              setEditing(false);
            },
          });
        }}
        onEdit={() => {
          setEditing(true);
        }}
      />
      <Footer taskId={taskId} confirmed={confirmed} />
    </>
  );
}

/**
 * The framework the Detailed Description is drafted in: its sections, Removed, coverage and
 * the yes. Every edit is the whole framework sent again; the server says who wrote what.
 * Confirmed, it holds still until the attorney chooses to edit it.
 */
export function FrameworkPage({ taskId }: { taskId: string }) {
  const query = useDdFramework(taskId);
  const { data } = query;
  const problem = (
    <ReadProblem query={query} what="the framework" className="mt-6" />
  );
  if (data === undefined) {
    return query.isError ? (
      problem
    ) : (
      <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner size="sm" /> Reading the framework…
      </div>
    );
  }
  return (
    <>
      {problem}
      <Loaded taskId={taskId} data={data} />
    </>
  );
}
